// grok-orders-20261021: one shared live signal bus for the charted coin.
// Fed synchronously from useChartData's onRawTrade (same call stack as the
// trade landing in the chart's buffer) plus the chart's Liquidity / ARD
// window events. Armed orders subscribe for instant evaluation; Grok reads
// snapshot() through chartContext(). Keeps ~5 min of trades, nothing else.
import { onRawTrade } from '../components/hooks/useChartData'

const KEEP_MS = 5 * 60 * 1000
const S = {
  token: null, trades: [],          // { t, side: 1|-1, sol, w, price, mcap }
  lastLiq: null, lastArd: null,     // { dir, sec?, at }
  orderStatus: null, me: null,
  subs: new Set(),
}

const emit = (ev) => { for (const fn of S.subs) { try { fn(ev) } catch {} } }
export function subscribeSignals(fn) { S.subs.add(fn); return () => S.subs.delete(fn) }
export function setOrderStatus(text) { S.orderStatus = text || null }
export function setMyWallet(addr) { S.me = addr || null }

const resetFor = (token) => { S.token = token; S.trades.length = 0; S.lastLiq = null; S.lastArd = null }

try {
  onRawTrade((token, wallet, side, sol, price, t, mcap) => {
    if (!token) return
    if (token !== S.token) resetFor(token)
    const now = Date.now()
    const tr = S.trades
    tr.push({ t: now, side: side === 'buy' ? 1 : -1, sol: +sol || 0, w: wallet || '', price: +price || 0, mcap: +mcap || 0 })
    while (tr.length && tr[0].t < now - KEEP_MS) tr.shift()
    emit({ kind: 'trade', token, wallet: wallet || '', side, sol: +sol || 0, price: +price || 0, mcap: +mcap || 0, t: now })
  })
} catch {}

if (typeof window !== 'undefined') {
  window.addEventListener('solcheddar-liq-reaction', (e) => {
    try {
      const d = e.detail || {}
      if (!d.token || d.token !== S.token) return
      // `at` is tape time; put it on the client clock and ignore stale labels
      // (history loads / cached cells re-emit old reactions).
      const off = Number.isFinite(d.tapeAt) && Number.isFinite(d.clientAt) ? d.clientAt - d.tapeAt : 0
      const at = Number.isFinite(d.at) ? d.at + off : Date.now()
      if (Date.now() - at > 15000) return
      const dir = d.dir === 'up' ? 'up' : d.dir === 'down' ? 'down' : null
      if (!dir || !(d.sec >= 0)) return
      S.lastLiq = { dir, sec: +d.sec, at }
      emit({ kind: 'liq', token: S.token, dir, sec: +d.sec, t: Date.now() })
    } catch {}
  })
  window.addEventListener('solcheddar-ard-triangle', (e) => {
    try {
      const d = e.detail || {}
      if (!d.token || d.token !== S.token) return
      const dir = d.dir === 'LONG' || d.dir === 'PROTECT' ? d.dir : null
      if (!dir) return
      S.lastArd = { dir, at: Date.now() }
      emit({ kind: 'ard', token: S.token, dir, t: Date.now() })
    } catch {}
  })
}

// ── stats (outside wallets only: the connected wallet's own prints skipped) ──
function scan(win, fn) {
  const tr = S.trades, from = Date.now() - win * 1000
  for (let i = tr.length - 1; i >= 0; i--) {
    const x = tr[i]
    if (x.t < from) break
    if (S.me && x.w === S.me) continue
    fn(x)
  }
}
export function stat(ind, win = 60) {
  switch (ind) {
    case 'buySol': { let s = 0; scan(win, x => { if (x.side > 0) s += x.sol }); return s }
    case 'sellSol': { let s = 0; scan(win, x => { if (x.side < 0) s += x.sol }); return s }
    case 'netSol': { let s = 0; scan(win, x => { s += x.side * x.sol }); return s }
    case 'maxBuy': { let m = 0; scan(win, x => { if (x.side > 0 && x.sol > m) m = x.sol }); return m }
    case 'maxSell': { let m = 0; scan(win, x => { if (x.side < 0 && x.sol > m) m = x.sol }); return m }
    case 'trades': { let n = 0; scan(win, () => { n++ }); return n }
    case 'mcap': { const l = S.trades[S.trades.length - 1]; return l ? l.mcap : 0 }
    case 'mcapChgPct': {
      const tr = S.trades; if (!tr.length) return 0
      const from = Date.now() - win * 1000
      let first = null
      for (let i = tr.length - 1; i >= 0; i--) { if (tr[i].t < from) break; if (tr[i].mcap > 0) first = tr[i].mcap }
      const last = tr[tr.length - 1].mcap
      return first > 0 && last > 0 ? (last / first - 1) * 100 : 0
    }
    case 'liqAgeSec': return S.lastLiq ? (Date.now() - S.lastLiq.at) / 1000 : 1e9
    case 'ardAgeSec': return S.lastArd ? (Date.now() - S.lastArd.at) / 1000 : 1e9
    default: return NaN
  }
}

const r = (v, d = 3) => (Number.isFinite(v) ? +v.toFixed(d) : null)
// Compact snapshot for Grok (rides every chat request; keep it small).
export function signalSnapshot() {
  try {
    if (!S.token) return null
    return {
      token: S.token,
      mcap: r(stat('mcap'), 2),
      mcap_chg_pct_60s: r(stat('mcapChgPct', 60), 2),
      mcap_chg_pct_300s: r(stat('mcapChgPct', 300), 2),
      buy_sol_10s: r(stat('buySol', 10)), sell_sol_10s: r(stat('sellSol', 10)),
      buy_sol_60s: r(stat('buySol', 60)), sell_sol_60s: r(stat('sellSol', 60)),
      max_buy_60s: r(stat('maxBuy', 60)), max_sell_60s: r(stat('maxSell', 60)),
      trades_60s: stat('trades', 60),
      liq_dir: S.lastLiq?.dir || null, liq_sec: S.lastLiq ? r(S.lastLiq.sec, 2) : null,
      liq_age_s: S.lastLiq ? r(stat('liqAgeSec'), 1) : null,
      ard_dir: S.lastArd?.dir || null, ard_age_s: S.lastArd ? r(stat('ardAgeSec'), 1) : null,
      order_status: S.orderStatus,
    }
  } catch { return null }
}
